import { useMemo, useCallback, useRef } from 'react';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import useControlProp from '../../hooks/useControlProp';

export default function usePagination({
  /**
   * When `manual` is `true`, it indicates that the component implementing this hook is fetching data for each page,
   * So `data` represents data only for the current page.
   */
  manual = false,
  data = [],
  totalSize: currentTotalSize = data.length,
  page: pageProp,
  pageSize: pageSizeProp,
  onPageChange = () => {},
  initialPage = 1,
  onPageSizeChange,
}) {
  const total = useRef(0);
  const [page, setPage] = useControlProp(pageProp, onPageChange, initialPage);
  const [pageSize, setPageSizeState] = useControlProp(pageSizeProp, onPageSizeChange, 10);

  if (currentTotalSize) {
    total.current = currentTotalSize;
  }
  const totalSize = total.current;

  const pageData = useMemo(() => {
    if (manual) {
      return data;
    }
    const pageStart = pageSize * (page - 1);
    const pageEnd = pageStart + pageSize;

    return data.slice(pageStart, pageEnd);
  }, [data, manual, page, pageSize]);

  const pageCount = Math.ceil(totalSize / pageSize);

  const itemCountOnPage = totalSize
    ? Math.min(pageSize, totalSize - pageSize * (page - 1))
    : pageSize;

  const previousPage = useCallback(() => {
    return setPage((old) => Math.max(old - 1, 1));
  }, [setPage]);

  const nextPage = useCallback(() => {
    return setPage((old) => Math.min(old + 1), pageCount);
  }, [pageCount, setPage]);

  const setPageSize = useCallback(
    (size) => {
      setPageSizeState(size);
    },
    [setPageSizeState],
  );

  useUpdateEffect(() => {
    if (page > pageCount) setPage(1);
  }, [pageCount, page]);

  const canPreviousPage = page > 0;
  const canNextPage = page < pageCount - 1;

  return {
    pageData,
    page,
    pageSize,
    pageCount,
    canPreviousPage,
    canNextPage,
    gotoPage: setPage,
    setPageSize,
    previousPage,
    nextPage,
    itemCountOnPage,
    totalSize,
  };
}
